import React, { useEffect } from "react";

/**
 * In-app confirmation dialog. Use instead of window.confirm.
 * @param {boolean} open - Whether the modal is visible
 * @param {string} title - Modal title
 * @param {string} message - Body text
 * @param {string} confirmLabel - Confirm button label (e.g. "Delete")
 * @param {string} cancelLabel - Cancel button label
 * @param {() => void} onConfirm - Called when user confirms
 * @param {() => void} onCancel - Called when user cancels or clicks backdrop
 * @param {boolean} danger - If true, confirm button uses danger (red) style
 */
export default function ConfirmModal({
  open,
  title = "Confirm",
  message = "",
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  danger = false,
}) {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", handleEscape);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: "var(--surface-overlay)" }}
      onClick={(e) => e.target === e.currentTarget && onCancel?.()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className="w-full max-w-md rounded-lg border p-6"
        style={{
          backgroundColor: "var(--surface-raised)",
          borderColor: "var(--border-default)",
          boxShadow: "var(--shadow-lg)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-modal-title"
          className="mb-2 text-[15px] font-[450] tracking-tight"
          style={{ color: "var(--text-primary)" }}
        >
          {title}
        </h2>
        <p className="mb-6 text-[12px] leading-snug" style={{ color: "var(--text-muted)" }}>
          {message}
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors"
            style={{
              color: "var(--text-muted)",
              backgroundColor: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => onConfirm?.()}
            className="rounded-md px-3 py-1.5 text-[12px] font-medium transition-colors"
            style={{
              color: "var(--text-on-brand)",
              backgroundColor: danger ? "var(--feedback-error)" : "var(--brand-primary)",
              border: "none",
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
